<template>
  <div
    class="tweet border rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-card text-card-foreground cursor-pointer h-full flex flex-col"
    @click="$emit('click', $event)"
  >
    <!-- Media (if available) - Fixed height for consistency -->
    <div v-if="hasMedia" class="relative w-full" style="flex: 0 0 auto">
      <AspectRatio :ratio="1">
        <div class="w-full h-full bg-muted flex items-center justify-center overflow-hidden">
          <!-- Show image for photos or video thumbnails -->
          <NuxtImg
            v-if="coverImageSrc"
            :src="coverImageSrc"
            :alt="tweet.author?.[0] || 'Tweet media'"
            class="w-full h-full object-cover transition-transform duration-300 ease-in-out"
            loading="lazy"
          />
          <!-- Fallback for tweets with media but no preview -->
          <div v-else class="flex flex-col items-center justify-center h-full w-full p-4">
            <Icon name="heroicons:photo" class="w-12 h-12 text-muted-foreground opacity-30 mb-2" />
            <p class="text-sm text-muted-foreground text-center">Media preview not available</p>
          </div>

          <!-- Media count indicator -->
          <div
            v-if="totalMediaCount > 1"
            class="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-md text-sm"
          >
            +{{ totalMediaCount - 1 }}
          </div>

          <!-- Video indicator -->
          <div
            v-if="hasVideo"
            class="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-md text-sm flex items-center gap-1"
          >
            <Icon name="heroicons:play" class="w-4 h-4" />
            <span>Video</span>
          </div>
        </div>
      </AspectRatio>
    </div>

    <!-- Text-only tweet content - Expanded to fill the card -->
    <div v-if="!hasMedia" class="text-only-content flex-1 flex flex-col p-4">
      <div class="flex justify-between items-start mb-3">
        <TweetAuthor
          :author="tweet.author?.[0]"
          :handle="tweet.authorHandle"
          :author-pic-src="authorPicSrc"
        />
        <span class="text-sm text-muted-foreground">{{ formatDate(tweet.tweetCreatedAt) }}</span>
      </div>

      <div class="flex-1 flex items-center justify-center">
        <div class="text-content">
          <!-- <Icon
            name="heroicons:chat-bubble-bottom-center-text"
            class="w-12 h-12 text-muted-foreground opacity-30 mx-auto mb-4"
          /> -->
          <TweetText :text="tweet.tweetRootText" class="text-lg text-center" />
        </div>
      </div>
    </div>

    <!-- Content for media tweets - Fixed height for consistency -->
    <div v-else class="p-4 flex-1 flex flex-col overflow-hidden">
      <!-- Header with Author and Date -->
      <div class="flex justify-between items-start mb-3">
        <TweetAuthor
          :author="tweet.author?.[0]"
          :handle="tweet.authorHandle"
          :author-pic-src="authorPicSrc"
        />
        <span class="text-sm text-muted-foreground">{{ formatDate(tweet.tweetCreatedAt) }}</span>
      </div>

      <!-- Tweet Text -->
      <div class="flex-1 overflow-hidden">
        <TweetText :text="truncateText(tweet.tweetRootText)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TwitterBubble } from '@/stores/interfaces'
import { useMediaItems } from '@/composables/useMediaItems'
import TweetText from '@/components/tweet/TweetText.vue'
import TweetAuthor from '@/components/tweet/TweetAuthor.vue'
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { useRuntimeConfig } from '#app'

defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const props = defineProps<{
  tweet: TwitterBubble
}>()

const config = useRuntimeConfig()
const dataStore = config.public.dataStore

const { getAuthorPicSrc, getRootMediaItems, getQuoteMediaItems } = useMediaItems()

const authorPicSrc = computed(() => getAuthorPicSrc(props.tweet))
const rootMediaItems = computed(() => getRootMediaItems(props.tweet))
const quoteMediaItems = computed(() => getQuoteMediaItems(props.tweet))

// Show root media first, fallback to quote media if no root media exists
const mediaToShow = computed(() => {
  if (rootMediaItems.value.length > 0) return rootMediaItems.value
  return quoteMediaItems.value
})

// Get the cover image source (first image or video thumbnail)
const coverImageSrc = computed(() => {
  // First check for video cover photos in offline resources
  if (props.tweet.offline && props.tweet.offlineMediaResources) {
    const offlineResources = props.tweet.offlineMediaResources as Record<
      string,
      Record<string, string>
    >

    // Get all possible tweet IDs to check
    const possibleTweetIds = [
      props.tweet.tweet_id, // Direct tweet ID
      ...(props.tweet.thread?.map((t) => t.tweet_id) || []), // IDs from thread
    ].filter((id): id is string => Boolean(id))

    // Try each tweet ID to find media resources
    for (const tweetId of possibleTweetIds) {
      const resources = offlineResources[tweetId]
      if (resources && typeof resources === 'object') {
        const resourcesObj = resources as Record<string, string>

        // Look for video cover photos
        const coverPhotoKeys = Object.keys(resourcesObj).filter((key) =>
          key.includes('video_cover_photo')
        )

        const firstCoverPhotoKey = coverPhotoKeys[0]
        if (firstCoverPhotoKey && firstCoverPhotoKey in resourcesObj) {
          return `${dataStore}${resourcesObj[firstCoverPhotoKey]}`
        }

        // Then check for image resources
        const imageKeys = Object.keys(resourcesObj).filter((key) => key.includes('image_'))

        const firstImageKey = imageKeys[0]
        if (firstImageKey && firstImageKey in resourcesObj) {
          return `${dataStore}${resourcesObj[firstImageKey]}`
        }
      }
    }

    // Check for flat structure (fallback)
    const coverPhotoKeys = Object.keys(offlineResources).filter((key) =>
      key.includes('video_cover_photo')
    )

    const firstCoverPhotoKey = coverPhotoKeys[0]
    if (firstCoverPhotoKey && firstCoverPhotoKey in offlineResources) {
      const value = offlineResources[firstCoverPhotoKey]
      if (typeof value === 'string') {
        return `${dataStore}${value}`
      }
    }

    // Then check for image resources in flat structure
    const imageKeys = Object.keys(offlineResources).filter(
      (key) => key.includes('image_') && key !== 'profile_pic'
    )

    const firstImageKey = imageKeys[0]
    if (firstImageKey && firstImageKey in offlineResources) {
      const value = offlineResources[firstImageKey]
      if (typeof value === 'string') {
        return `${dataStore}${value}`
      }
    }
  }

  // Fallback to online media
  if (mediaToShow.value.length > 0 && mediaToShow.value[0]?.src) {
    return mediaToShow.value[0].src
  }

  return null
})

function truncateText(text: string | undefined) {
  if (!text) return ''
  const maxLength = 120 // Keep text short and consistent
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

function formatDate(dateString: string | undefined) {
  if (!dateString) return ''
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateString))
}

const totalMediaCount = computed(() => {
  let count = rootMediaItems.value.length + quoteMediaItems.value.length

  // Add offline media count
  if (props.tweet.offline && props.tweet.offlineMediaResources) {
    const offlineResources = props.tweet.offlineMediaResources as Record<
      string,
      Record<string, string>
    >

    // Check for nested structure with tweet_id
    if (props.tweet.tweet_id && props.tweet.tweet_id in offlineResources) {
      const tweetResources = offlineResources[props.tweet.tweet_id]
      if (tweetResources && typeof tweetResources === 'object') {
        const resourcesObj = tweetResources as Record<string, string>

        const videoCount = Object.keys(resourcesObj).filter(
          (key) => key.includes('video_') && !key.includes('cover_photo')
        ).length

        const imageCount = Object.keys(resourcesObj).filter((key) => key.includes('image_')).length

        count = Math.max(count, videoCount + imageCount)
      }
    } else {
      // Flat structure
      const videoCount = Object.keys(offlineResources).filter(
        (key) => key.includes('video_') && !key.includes('cover_photo') && key !== 'profile_pic'
      ).length

      const imageCount = Object.keys(offlineResources).filter(
        (key) => key.includes('image_') && key !== 'profile_pic'
      ).length

      count = Math.max(count, videoCount + imageCount)
    }
  }

  return count
})

// Check if there's any media
const hasMedia = computed(() => {
  // Check online media
  if (mediaToShow.value.length > 0) return true

  // Check offline media resources
  if (props.tweet.offline && props.tweet.offlineMediaResources) {
    const offlineResources = props.tweet.offlineMediaResources as Record<
      string,
      Record<string, string>
    >

    // Get all possible tweet IDs to check
    const possibleTweetIds = [
      props.tweet.tweet_id, // Direct tweet ID
      ...(props.tweet.thread?.map((t) => t.tweet_id) || []), // IDs from thread
    ].filter(Boolean) as string[] // Remove undefined/null values

    // Try each tweet ID to find media resources
    for (const tweetId of possibleTweetIds) {
      const resources = offlineResources[tweetId]
      if (resources) {
        if (
          Object.keys(resources).some((key) => key.includes('video_') || key.includes('image_'))
        ) {
          return true
        }
      }
    }

    // Check for flat structure
    return Object.keys(offlineResources).some(
      (key) => (key.includes('video_') || key.includes('image_')) && key !== 'profile_pic'
    )
  }

  return false
})

// Check if there's any video
const hasVideo = computed(() => {
  // Check online media
  if (mediaToShow.value.some((item) => item.type === 'video' || item.type === 'gif')) {
    return true
  }

  // Check offline resources for videos
  if (props.tweet.offline && props.tweet.offlineMediaResources) {
    const offlineResources = props.tweet.offlineMediaResources as Record<
      string,
      Record<string, string>
    >

    // Get all possible tweet IDs to check
    const possibleTweetIds = [
      props.tweet.tweet_id, // Direct tweet ID
      ...(props.tweet.thread?.map((t) => t.tweet_id) || []), // IDs from thread
    ].filter(Boolean) as string[] // Remove undefined/null values

    // Try each tweet ID to find video resources
    for (const tweetId of possibleTweetIds) {
      const resources = offlineResources[tweetId]
      if (resources) {
        if (
          Object.keys(resources).some(
            (key) => key.includes('video_') && !key.includes('cover_photo')
          )
        ) {
          return true
        }
      }
    }

    // Check for flat structure
    return Object.keys(offlineResources).some(
      (key) => key.includes('video_') && !key.includes('cover_photo') && key !== 'profile_pic'
    )
  }

  return false
})
</script>

<style scoped>
.tweet {
  min-height: 200px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tweet:hover img {
  transform: scale(1.05);
}

.text-only-content {
  height: 100%;
}

.text-content {
  max-width: 90%;
  margin: 0 auto;
}
</style>
